import { useEffect } from 'react';
import { useT } from '../i18n';

interface Props {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual tone for the primary action. */
  tone?: 'accent' | 'danger' | 'ok';
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

const TONE_COLORS = {
  accent: { bg: 'var(--accent)', hover: 'var(--accent)' },
  danger: { bg: 'var(--err)', hover: 'var(--err)' },
  ok:     { bg: 'var(--ok)',    hover: 'var(--ok)'    },
} as const;

export function ConfirmDialog({
  open, title, message,
  confirmLabel,
  cancelLabel,
  tone = 'accent',
  onConfirm, onCancel,
  busy = false,
}: Props) {
  const { t } = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && !busy) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel, onConfirm]);

  if (!open) return null;

  const colors = TONE_COLORS[tone];
  const finalConfirmLabel = confirmLabel ?? t('common.confirm');
  const finalCancelLabel = cancelLabel ?? t('common.cancel');

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 'var(--s16)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--surface)',
          width: 'min(420px, 100%)',
          borderRadius: 'var(--r1)',
          boxShadow: '0 10px 32px rgba(0,0,0,0.30)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: 'var(--s12) var(--s16)',
          borderBottom: '1px solid var(--line)',
          fontSize: 'var(--t-15)', fontWeight: 700, color: 'var(--ink-1)',
        }}>
          {title}
        </div>

        {message && (
          <div style={{
            padding: 'var(--s12) var(--s16)',
            fontSize: 'var(--t-13)', color: 'var(--ink-2)',
            lineHeight: 1.5,
          }}>
            {message}
          </div>
        )}

        <div style={{
          padding: 'var(--s12) var(--s16)',
          borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'flex-end', gap: 'var(--s8)',
          background: 'var(--bg)',
        }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="sort-btn"
            style={{ cursor: busy ? 'wait' : 'pointer' }}
          >
            {finalCancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            style={{
              padding: 'var(--s6) var(--s14)',
              borderRadius: 'var(--r2)',
              border: 'none',
              background: colors.bg,
              color: 'var(--surface)',
              fontSize: 'var(--t-12)',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
            }}
            autoFocus
          >
            {busy ? '…' : finalConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
